import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plug, AlertTriangle, CheckCircle2, Settings,
  ExternalLink, Plus, X, Save, GitBranch,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select as ShSelect, SelectContent as ShSelectContent, SelectItem as ShSelectItem,
  SelectTrigger as ShSelectTrigger, SelectValue as ShSelectValue,
} from "@/components/ui/select";
import TopNav from "@/components/TopNav";
import {
  agentMcpRequired,
  agentMcpUpsert,
  agentMcpDelete,
  mcpCatalog,
  adminMcpCustomCreate,
} from "@/lib/api";

function Badge({ tone = "slate", children, testid }) {
  const tones = {
    slate: "bg-slate-100 border-slate-300 text-slate-700",
    emerald: "bg-emerald-100 border-emerald-300 text-emerald-800",
    amber: "bg-amber-100 border-amber-300 text-amber-800",
    blue: "bg-accent-blue/10 border-accent-blue/40 text-accent-blue",
    red: "bg-red-100 border-red-300 text-red-700",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function ConfigureModal({ open, mcp, slug, onClose, onSaved }) {
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setConfig({});
  }, [open, mcp]);

  if (!open || !mcp) return null;
  const fields = mcp.config_fields || [];

  const save = async () => {
    setSaving(true);
    try {
      await agentMcpUpsert(slug, mcp.id, config);
      toast.success(`${mcp.label} configurado.`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4">
      <div className="w-full max-w-xl rounded-sm bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 p-6">
          <div>
            <p className="eyebrow text-accent-blue">
              <Plug className="mr-1 inline h-3 w-3" /> Configurar MCP
            </p>
            <h2 className="mt-1 font-display text-2xl text-ink">{mcp.label}</h2>
            <p className="mt-1 text-[12px] text-slate-500">{mcp.category} · transporte {mcp.transport}</p>
            {mcp.github && (
              <a href={mcp.github} target="_blank" rel="noreferrer"
                 className="mt-2 inline-flex items-center gap-1 text-[11px] text-accent-blue hover:underline">
                <GitBranch className="h-3 w-3" /> {mcp.github} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-ink">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-6">
          {fields.length === 0 ? (
            <p className="text-[12px] text-slate-500">Este MCP no requiere credenciales.</p>
          ) : fields.map((f) => (
            <div key={f.key}>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                {f.label}{f.required ? <span className="text-red-500"> *</span> : null}
              </label>
              <Input
                type={f.type === "secret" ? "password" : (f.type === "number" ? "number" : "text")}
                placeholder={f.placeholder || ""}
                value={config[f.key] ?? ""}
                onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                className="mt-1 rounded-sm border-slate-300"
                data-testid={`mcp-field-${f.key}`}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <Button variant="outline" onClick={onClose}
                  className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]">
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving}
                  className="rounded-sm bg-emerald-600 text-[11px] uppercase tracking-[0.18em] text-white hover:bg-emerald-700"
                  data-testid="mcp-save-btn">
            {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
            Guardar credenciales
          </Button>
        </div>
      </div>
    </div>
  );
}

function CustomMcpModal({ open, prefill, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [github, setGithub] = useState("");
  const [transport, setTransport] = useState("stdio");
  const [command, setCommand] = useState("");
  const [url, setUrl] = useState("");
  const [keywords, setKeywords] = useState("");
  const [fields, setFields] = useState([{ id: "f0", key: "API_KEY", label: "API Key", type: "secret", required: true }]);
  const [saving, setSaving] = useState(false);
  const fieldIdRef = React.useRef(1);

  useEffect(() => {
    if (open) {
      setLabel(prefill?.evidence || "");
      setGithub("");
      setTransport("stdio");
      setCommand("");
      setUrl("");
      setKeywords(prefill?.evidence ? prefill.evidence.toLowerCase() : "");
      setFields([{ id: "f0", key: "API_KEY", label: "API Key", type: "secret", required: true }]);
      fieldIdRef.current = 1;
    }
  }, [open, prefill]);

  if (!open) return null;

  const save = async () => {
    if (!label.trim()) { toast.error("Etiqueta obligatoria."); return; }
    if (transport === "stdio" && !command.trim()) { toast.error("Comando obligatorio para stdio."); return; }
    if (transport !== "stdio" && !url.trim()) { toast.error("URL obligatoria para http/sse."); return; }
    setSaving(true);
    try {
      const d = await adminMcpCustomCreate({
        label: label.trim(),
        github: github.trim(),
        transport,
        command_template: command.trim(),
        url_template: url.trim(),
        keywords: keywords.split(",").map((s) => s.trim()).filter(Boolean),
        config_fields: fields.filter((f) => f.key.trim()),
        category: "Custom",
      });
      toast.success(`MCP "${d.mcp.label}" añadido al catálogo.`);
      onCreated && onCreated(d.mcp);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando MCP.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4">
      <div className="w-full max-w-2xl rounded-sm bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 p-6">
          <div>
            <p className="eyebrow text-accent-purple">
              <Plus className="mr-1 inline h-3 w-3" /> Custom MCP
            </p>
            <h2 className="mt-1 font-display text-2xl text-ink">Añadir MCP del catálogo de GitHub</h2>
            <p className="mt-1 max-w-xl text-[12px] text-slate-500">
              Pegue el URL de un MCP server publicado en GitHub. Quedará
              disponible en el catálogo para configurarlo en cualquier agente.
              {prefill?.evidence && <> Sistema detectado: <strong className="text-ink">"{prefill.evidence}"</strong>.</>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-ink">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Etiqueta</label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)}
                     placeholder="Avaya CallControl, NetSuite…"
                     className="mt-1 rounded-sm border-slate-300"
                     data-testid="custom-mcp-label-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">GitHub URL</label>
              <Input value={github} onChange={(e) => setGithub(e.target.value)}
                     placeholder="https://github.com/owner/repo"
                     className="mt-1 rounded-sm border-slate-300 font-mono text-[11px]"
                     data-testid="custom-mcp-github-input" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Transporte</label>
            <ShSelect value={transport} onValueChange={setTransport}>
              <ShSelectTrigger className="mt-1 h-10 rounded-sm border-slate-300"
                               data-testid="custom-mcp-transport-select">
                <ShSelectValue />
              </ShSelectTrigger>
              <ShSelectContent>
                <ShSelectItem value="stdio">stdio (proceso local, e.g. npx)</ShSelectItem>
                <ShSelectItem value="http">http (servidor remoto)</ShSelectItem>
                <ShSelectItem value="sse">sse (Server-Sent Events)</ShSelectItem>
              </ShSelectContent>
            </ShSelect>
          </div>
          {transport === "stdio" ? (
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Comando</label>
              <Input value={command} onChange={(e) => setCommand(e.target.value)}
                     placeholder="npx -y @owner/mcp-server"
                     className="mt-1 rounded-sm border-slate-300 font-mono text-[11px]"
                     data-testid="custom-mcp-command-input" />
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">URL del servidor</label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)}
                     placeholder="https://api.example.com/mcp"
                     className="mt-1 rounded-sm border-slate-300 font-mono text-[11px]"
                     data-testid="custom-mcp-url-input" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
              Palabras clave (separadas por coma) — para autodetectar este MCP en futuros ABC
            </label>
            <Input value={keywords} onChange={(e) => setKeywords(e.target.value)}
                   placeholder="avaya, callcontrol, telefonia"
                   className="mt-1 rounded-sm border-slate-300 font-mono text-[11px]" />
          </div>
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Campos de configuración</p>
              <button onClick={() => setFields((fs) => [...fs, { key: "", label: "", type: "text", required: false }])}
                      className="text-[11px] text-accent-blue hover:underline">+ añadir campo</button>
            </div>
            {fields.map((f, idx) => (
              <div key={f.id} className="mb-2 grid grid-cols-12 items-center gap-2">
                <Input value={f.key} placeholder="API_KEY"
                       onChange={(e) => setFields((fs) => fs.map((x, i) => i === idx ? { ...x, key: e.target.value } : x))}
                       className="col-span-3 h-8 rounded-sm border-slate-300 font-mono text-[10px]" />
                <Input value={f.label} placeholder="Etiqueta visible"
                       onChange={(e) => setFields((fs) => fs.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                       className="col-span-5 h-8 rounded-sm border-slate-300 text-[11px]" />
                <ShSelect value={f.type} onValueChange={(v) => setFields((fs) => fs.map((x, i) => i === idx ? { ...x, type: v } : x))}>
                  <ShSelectTrigger className="col-span-3 h-8 rounded-sm border-slate-300 text-[11px]">
                    <ShSelectValue />
                  </ShSelectTrigger>
                  <ShSelectContent>
                    <ShSelectItem value="text">text</ShSelectItem>
                    <ShSelectItem value="secret">secret</ShSelectItem>
                    <ShSelectItem value="number">number</ShSelectItem>
                  </ShSelectContent>
                </ShSelect>
                <button onClick={() => setFields((fs) => fs.filter((x) => x.id !== f.id))}
                        className="col-span-1 text-red-500 hover:text-red-700">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <Button variant="outline" onClick={onClose}
                  className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]">
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving}
                  className="rounded-sm bg-gradient-to-r from-accent-blue to-accent-purple text-[11px] uppercase tracking-[0.18em] text-white"
                  data-testid="custom-mcp-save-btn">
            {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
            Guardar custom MCP
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AgentMcps() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [configuring, setConfiguring] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customPrefill, setCustomPrefill] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([agentMcpRequired(slug), mcpCatalog()]);
      setData(d);
      setCatalog(c.mcp_servers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando MCPs.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [slug]);

  const removeCred = async (mcp_id) => {
    if (!window.confirm(`Quitar configuración de ${mcp_id} para este agente?`)) return;
    try {
      await agentMcpDelete(slug, mcp_id);
      toast.success("Configuración eliminada.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando.");
    }
  };

  // Group requirements by activity for cleaner display
  const grouped = useMemo(() => {
    const g = new Map();
    (data?.required || []).forEach((r) => {
      const key = `${r.department}\u2022${r.activity}`;
      if (!g.has(key)) g.set(key, { department: r.department, activity: r.activity, roles: r.roles, items: [] });
      else if (r.roles?.length) {
        const cur = g.get(key);
        cur.roles = Array.from(new Set([...(cur.roles || []), ...r.roles]));
      }
      g.get(key).items.push(r);
    });
    return Array.from(g.values());
  }, [data]);

  const catalogById = useMemo(
    () => Object.fromEntries(catalog.map((c) => [c.id, c])),
    [catalog],
  );

  return (
    <div className="min-h-screen bg-white text-ink">
      <TopNav />
      <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <Link to={`/agentes/${slug}/editar`}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-slate-500 hover:text-ink"
              data-testid="back-to-agent-edit">
          <ArrowLeft className="h-3 w-3" /> Volver al agente
        </Link>

        <p className="eyebrow mt-6 flex items-center gap-1.5">
          <Plug className="h-3 w-3" /> MCP · Integraciones
        </p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">
          MCPs requeridos por este agente
        </h1>
        {data?.agent && (
          <p className="mt-3 max-w-3xl text-sm text-slate-600">
            Inferidos del análisis ABC del agente{" "}
            <strong className="text-ink">{data.agent.name}</strong> (departamento{" "}
            <strong className="text-ink">{data.agent.department}</strong>). Cada bloque
            detalla <strong>la actividad</strong> que requiere el MCP y{" "}
            <strong>el recurso humano</strong> que lo opera.
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : grouped.length === 0 ? (
          <div className="mt-10 rounded-sm border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500"
               data-testid="mcps-empty">
            No se detectaron sistemas con MCP conocido en este departamento.
            Use "Añadir custom MCP" en la sección inferior para registrar uno manualmente.
          </div>
        ) : (
          <div className="mt-10 space-y-6">
            {grouped.map((group) => (
              <article key={`${group.department}|${group.activity}`}
                       className="border border-slate-200 bg-white"
                       data-testid={`mcp-group-${group.department}-${group.activity}`}>
                <header className="border-b border-slate-100 bg-slate-50 px-5 py-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                    {group.department}
                  </p>
                  <h3 className="mt-0.5 font-display text-lg text-ink">
                    {group.activity}
                  </h3>
                  {group.roles?.length > 0 && (
                    <p className="mt-1 text-[12px] text-slate-600">
                      Recurso humano:{" "}
                      <span className="font-medium text-ink">{group.roles.join(", ")}</span>
                    </p>
                  )}
                </header>
                <div className="divide-y divide-slate-100">
                  {group.items.map((r) => (
                    <div key={`${r.mcp_id}|${r.evidence}`}
                         className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-sm bg-gradient-to-br from-accent-blue to-accent-purple text-white">
                          <Plug className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-display text-base text-ink">{r.label}</p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
                            {r.category} · {r.transport} · detectado por: <span className="text-accent-blue">"{r.evidence}"</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.configured_agent ? (
                          <Badge tone="emerald"><CheckCircle2 className="mr-1 inline h-3 w-3" /> Configurado</Badge>
                        ) : r.configured_global ? (
                          <Badge tone="blue">Global</Badge>
                        ) : (
                          <Badge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> Sin configurar</Badge>
                        )}
                        <Button onClick={() => setConfiguring(catalogById[r.mcp_id] || { id: r.mcp_id, label: r.label, config_fields: [], transport: r.transport, github: r.github, category: r.category })}
                                size="sm"
                                className="rounded-sm bg-ink text-[10px] uppercase tracking-[0.16em] text-white hover:bg-ink-2"
                                data-testid={`configure-mcp-${r.mcp_id}`}>
                          <Settings className="mr-1 h-3 w-3" /> Configurar
                        </Button>
                        {r.configured_agent && (
                          <button onClick={() => removeCred(r.mcp_id)}
                                  className="text-[10px] text-red-600 hover:underline">
                            quitar
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Unmatched systems */}
        {data?.unmatched_systems?.length > 0 && (
          <section className="mt-14">
            <p className="eyebrow text-amber-700">
              <AlertTriangle className="mr-1 inline h-3 w-3" /> Sistemas sin MCP en catálogo
            </p>
            <h2 className="mt-2 font-display text-2xl text-ink">
              Sistemas detectados sin integración disponible
            </h2>
            <p className="mt-1 max-w-2xl text-[12px] text-slate-500">
              Estos sistemas fueron mencionados en el análisis ABC pero no
              tienen un MCP en el catálogo. Puede registrarlos manualmente
              pegando el URL de un MCP server publicado en GitHub.
            </p>
            <div className="mt-5 divide-y divide-slate-200 border border-slate-200">
              {data.unmatched_systems.map((u) => (
                <div key={`${u.evidence}|${u.department}|${u.activity}`}
                     className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                     data-testid={`unmatched-${u.evidence}`}>
                  <div>
                    <p className="font-display text-base text-ink">{u.evidence}</p>
                    <p className="text-[12px] text-slate-500">
                      {u.activity} {u.roles?.length ? `· ${u.roles.join(", ")}` : ""}
                    </p>
                  </div>
                  <Button size="sm" onClick={() => { setCustomPrefill(u); setCustomOpen(true); }}
                          className="rounded-sm bg-accent-blue text-[10px] uppercase tracking-[0.16em] text-white hover:opacity-90"
                          data-testid={`add-custom-mcp-${u.evidence}`}>
                    <Plus className="mr-1 h-3 w-3" /> Añadir como custom MCP
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14">
          <div className="flex items-center justify-between">
            <div>
              <p className="eyebrow">Catálogo de MCPs disponibles</p>
              <h2 className="mt-2 font-display text-2xl text-ink">
                Todos los MCPs ({catalog.length})
              </h2>
            </div>
            <Button onClick={() => { setCustomPrefill(null); setCustomOpen(true); }}
                    className="rounded-sm bg-gradient-to-r from-accent-blue to-accent-purple text-[11px] uppercase tracking-[0.18em] text-white"
                    data-testid="open-custom-mcp-btn">
              <Plus className="mr-2 h-3.5 w-3.5" /> Añadir custom MCP
            </Button>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {catalog.map((c) => (
              <article key={c.id} className="border border-slate-200 bg-white p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{c.category}</p>
                <p className="mt-0.5 font-display text-base text-ink">{c.label}</p>
                {c.github && (
                  <a href={c.github} target="_blank" rel="noreferrer"
                     className="mt-1 inline-flex items-center gap-1 text-[10px] text-accent-blue hover:underline">
                    <GitBranch className="h-2.5 w-2.5" /> github <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>
      </div>

      <ConfigureModal
        open={!!configuring}
        mcp={configuring}
        slug={slug}
        onClose={() => setConfiguring(null)}
        onSaved={load}
      />
      <CustomMcpModal
        open={customOpen}
        prefill={customPrefill}
        onClose={() => setCustomOpen(false)}
        onCreated={() => load()}
      />
    </div>
  );
}
